import React, { useContext, useEffect } from 'react';
import { Col, Row } from '@thd-olt-component-react/grid';
import { LayoutGroup, LayoutManager } from '@thd-olt-component-react/layout-manager';
import { useParams, useLocation } from '@thd-olt-component-react/router';
import { ArticleInfo } from '@thd-olt-component-react/article-info';
import { ToolsMaterials } from '@thd-olt-component-react/tools-materials';
import { RelatedArticles } from '@thd-olt-component-react/related-articles';
import { ArticleSections } from '@thd-olt-component-react/article-section';
import { ArticleGenLead } from '@thd-olt-component-react/home-services';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { AuthorTitle } from '@thd-olt-component-react/author-pod';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { Metadata } from '@thd-olt-component-react/metadata';
import { SearchFeedback } from '@thd-olt-component-react/search-feedback';
import {
  extend,
  params,
  shape,
  arrayOf,
  string as stringType,
  useDataModel,
  QueryContext,
} from '@thd-nucleus/data-sources';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';

export const Article = () => {
  declareContexts([ExperienceContext, QueryContext]);
  const { articleGUID } = useParams();

  const { data, loading, error } = useDataModel('article', {
    variables: {
      articleGUID,
    },
  });

  let breadCrumbs = data?.article?.breadcrumbs || [];
  let metadata = data?.article?.metadata || {};
  let schemaTag = data?.article?.schemaTag;
  const path = useLocation().pathname;

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (data && !loading) {
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('article-experience.page-load', {
          articleGUID,
          breadCrumbs,
        });
      }
    }
  }, []);

  if (!data || loading || error) {
    return null;
  }

  const isInfluencer = data?.article?.articleType === 'ap';

  return (
    <LayoutManager name="article-wrapper">
      <LayoutGroup name="metadata">
        <script type="application/ld+json">{schemaTag}</script>
        <Metadata data={metadata} />
      </LayoutGroup>
      <Row className="isBound">
        <Col xs="12" sm="12" md="12" lg="12" className="article-breadcrumbs">
          <LayoutGroup name="breadcrumbs">
            <Breadcrumbs staticCrumbs={breadCrumbs} />
          </LayoutGroup>
        </Col>
      </Row>
      <Row className="isBound">
        <Col xs="12" sm="12" md="12" lg="12" className="article-author-title">
          <LayoutGroup name="author-title">
            <AuthorTitle articleGUID={articleGUID} />
          </LayoutGroup>
        </Col>
      </Row>
      <Row className="isBound">
        <Col xs="12" className="article-info-sections">
          <Row>
            <LayoutGroup name="article-info">
              <ArticleInfo articleGUID={articleGUID} />
            </LayoutGroup>
          </Row>
          {isMobile && (
            <Row data-testid="mobile-tools" className="article-tools-materials__mobile">
              <LayoutGroup name="tools-materials">
                <ToolsMaterials articleGUID={articleGUID} />
              </LayoutGroup>
            </Row>
          )}
          <Row className="article-info-sections__section">
            <LayoutGroup name="article-sections">
              <ArticleSections articleGUID={articleGUID} />
            </LayoutGroup>
            {isInfluencer && (
              <LayoutGroup name="article-legal-disclaimer">
                <div className="article-disclaimer">
                  This post was created in partnership with The Home Depot. The author may have
                  received compensation for this article and associated images or video, potentially
                  in the form of products and/or services. All expressed opinions and experiences
                  are the author’s own words.
                </div>
              </LayoutGroup>
            )}
          </Row>
        </Col>
        <Col xs="12" sm="4" md="4" lg="4" className="article-tools-materials">
          {!isMobile && (
            <div data-testid="desktop-tools" className="article-tools-materials__container">
              <LayoutGroup name="tools-materials">
                <ToolsMaterials articleGUID={articleGUID} />
              </LayoutGroup>
            </div>
          )}
          <div className="article-related-articles">
            <LayoutGroup name="related-articles">
              <RelatedArticles articleGUID={articleGUID} />
            </LayoutGroup>
          </div>
          <LayoutGroup name="gen-lead">
            <ArticleGenLead articleGUID={articleGUID} />
          </LayoutGroup>
        </Col>
      </Row>
      <LayoutGroup
        tag={Row}
        className="u--paddingTop u--paddingBottom"
        name="article-feedback"
      >
        <Col>
          <SearchFeedback type="articles" />
        </Col>
      </LayoutGroup>
      <Row className="isBound">
        <EmtLinks path={path} />
      </Row>
    </LayoutManager>
  );
};

Article.propTypes = {};

Article.dataModel = extend(
  {
    article: params({
      articleGUID: stringType().isRequired(),
    }).shape({
      breadcrumbs: arrayOf(
        shape({
          dimensionName: stringType(),
          url: stringType(),
          label: stringType(),
        })
      ),
      title: stringType(),
      metadata: shape({
        description: stringType(),
        siteName: stringType(),
        title: stringType(),
        type: stringType(),
        heading: stringType(),
        canonicalURL: stringType(),
        facebookAdmins: stringType(),
      }),
      schemaTag: stringType(),
    }),
  },
  Breadcrumbs,
  AuthorTitle,
  ArticleGenLead,
  ArticleInfo,
  ToolsMaterials,
  ArticleSections,
  RelatedArticles,
  EmtLinks
);

Article.displayName = 'Article';

Article.defaultProps = {};
